import { notification } from 'antd'
import Quill from 'quill'
import { I18nT } from 'types'

const registeredQuills = new WeakMap<Quill, boolean>()

const registerQuillImageHandler = (quill: Quill | undefined, t: I18nT) => {
  if (!quill || registeredQuills.has(quill)) return

  const toolbar = quill.getModule('toolbar') as any
  if (!toolbar) return

  // Additional safety check on the toolbar instance itself
  if (toolbar.imageHandlerRegistered) return
  toolbar.imageHandlerRegistered = true

  registeredQuills.set(quill, true)

  const validateAndInsertImage = (file: File) => {
    const maxSize = 10 * 1024 * 1024 // 10MB

    // Validate size (10MB)
    if (file.size > maxSize) {
      notification.error({
        message: t('common.validationError.fileTooLarge10MB')
      })
      return
    }

    // Validate resolution (5000x5000)
    const reader = new FileReader()
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const img = new Image()
      img.onload = () => {
        if (img.width > 5000 || img.height > 5000) {
          notification.error({
            message: t('common.validationError.imageResolutionTooLarge')
          })
          return
        }
        const range = quill.getSelection()
        // Use setTimeout to decouple the insertion from the main event stack
        // to prevent "Maximum call stack size exceeded" errors
        setTimeout(() => {
          const index = range?.index ?? quill.getLength() - 1
          quill.insertEmbed(index, 'image', e.target?.result as string)
          // Move cursor after the image
          quill.setSelection(index + 1)
        }, 0)
      }
      img.src = e.target?.result as string
    }
    reader.readAsDataURL(file)
  }

  toolbar.addHandler('image', () => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')

    // Ensure the input is clean
    input.value = ''
    input.click()

    input.onchange = () => {
      const file = input.files?.[0]
      if (!file) return

      validateAndInsertImage(file)
    }
  })

  // Handle drag and drop images
  quill.root.addEventListener(
    'drop',
    (e: DragEvent) => {
      if (
        e.dataTransfer &&
        e.dataTransfer.files &&
        e.dataTransfer.files.length > 0
      ) {
        const file = e.dataTransfer.files[0]
        if (file.type.startsWith('image/')) {
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()
          validateAndInsertImage(file)
        }
      }
    },
    true
  )

  // Handle pasting images
  quill.root.addEventListener(
    'paste',
    (e: ClipboardEvent) => {
      if (
        e.clipboardData &&
        e.clipboardData.files &&
        e.clipboardData.files.length > 0
      ) {
        const file = e.clipboardData.files[0]
        if (file.type.startsWith('image/')) {
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()
          validateAndInsertImage(file)
        }
      }
    },
    true
  )
}
export default registerQuillImageHandler
